import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { differenceInCalendarDays, parseISO, format } from "date-fns";
import { es } from "date-fns/locale";
import { api } from "../lib/api";
import { ESTADOS, FUENTES_CONSULTA, TIPOS_EVENTO, getEstadoColor, getEstadoLabel, getFuenteLabel } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  Plus,
  Search,
  Calendar,
  MapPin,
  Phone,
  ArrowRight,
  Filter,
  Globe,
  Clock,
} from "lucide-react";

const parseEventDate = (value) => {
  if (!value) return null;
  try {
    return parseISO(value);
  } catch (error) {
    const dateOnly = value.split("T")[0];
    try {
      return parseISO(dateOnly);
    } catch (err) {
      return null;
    }
  }
};

const formatDate = (value) => {
  if (!value || value === "no hay datos" || value === "No hay datos") return value || "Sin fecha";
  const date = parseEventDate(value);
  if (!date) return value || "Sin fecha";
  try {
    return format(date, "dd-MM-yyyy", { locale: es });
  } catch (error) {
    return value || "Sin fecha";
  }
};

const calculateDaysRemaining = (fecha_evento) => {
  const eventDate = parseEventDate(fecha_evento);
  if (!eventDate) return null;
  return differenceInCalendarDays(eventDate, new Date());
};

const formatDaysRemaining = (days) => {
  if (days === null || days === undefined) return null;
  if (days > 1) return `Faltan ${days} días`;
  if (days === 1) return "Falta 1 día";
  if (days === 0) return "Es hoy";
  if (days === -1) return "Fue ayer";
  return `Hace ${Math.abs(days)} días`;
};

const getDiasHighlightStyles = (days) => {
  if (days === null || days === undefined) {
    return {
      card: "border border-border/30 bg-white",
      badge: "bg-slate-100 text-slate-600",
    };
  }
  if (days <= 15) {
    return {
      card: "border border-red-200 bg-red-50/60",
      badge: "bg-red-100 text-red-700",
    };
  }
  if (days <= 60) {
    return {
      card: "border border-orange-200 bg-orange-50/60",
      badge: "bg-orange-100 text-orange-700",
    };
  }
  if (days <= 90) {
    return {
      card: "border border-amber-200 bg-amber-50/60",
      badge: "bg-amber-100 text-amber-700",
    };
  }
  return {
    card: "border border-border/30 bg-white",
    badge: "bg-emerald-50 text-emerald-700",
  };
};



export default function ConsultasPage() {
  const [consultas, setConsultas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterEstado, setFilterEstado] = useState("");
  const [filterFuente, setFilterFuente] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    nombre: "",
    telefono: "",
    email: "",
    fecha_evento: "",
    tipo_evento: "",
    ubicacion: "",
    notas: "",
    fuente: "web",
  });
  const [submitting, setSubmitting] = useState(false);

  const loadConsultas = useCallback(async () => {
    try {
      const response = await api.getConsultas(filterEstado);
      setConsultas(response.data);
    } catch (error) {
      console.error("Error loading consultas:", error);
      toast.error("Error al cargar las consultas");
    } finally {
      setLoading(false);
    }
  }, [filterEstado]);

  useEffect(() => {
    loadConsultas();
  }, [loadConsultas, filterFuente]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await api.createConsulta(formData);
      toast.success("Consulta creada exitosamente");
      setDialogOpen(false);
      setFormData({
        nombre: "",
        telefono: "",
        email: "",
        fecha_evento: "",
        tipo_evento: "",
        ubicacion: "",
        notas: "",
        fuente: "web",
      });
      loadConsultas();
    } catch (error) {
      toast.error("Error al crear la consulta");
    } finally {
      setSubmitting(false);
    }
  };

  const filteredConsultas = consultas.filter((c) =>
    c.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.ubicacion.toLowerCase().includes(searchTerm.toLowerCase())
  ).filter((c) =>
    filterFuente ? c.fuente === filterFuente : true
  );

  const getDiasRestantes = (consulta) => {
    if (typeof consulta.dias_restantes === "number") return consulta.dias_restantes;
    return calculateDaysRemaining(consulta.fecha_evento);
  };

  // Group by estado for Kanban view
  const groupedConsultas = ESTADOS.reduce((acc, estado) => {
    acc[estado.value] = filteredConsultas.filter((c) => c.estado === estado.value);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-pulse text-primary font-heading text-xl">Cargando...</div>
      </div>
    );
  }

  return (
    <div data-testid="consultas-page" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl lg:text-4xl font-bold text-primary">
            Consultas
          </h1>
          <p className="text-muted-foreground mt-1">
            Gestiona todas las consultas de clientes potenciales
          </p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="new-consulta-btn"
              className="rounded-full bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20 gap-2"
            >
              <Plus className="w-4 h-4" />
              Nueva Consulta
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Nueva Consulta</DialogTitle>
              <DialogDescription>
                Añade los datos del cliente potencial
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="nombre">Nombre *</Label>
                  <Input
                    id="nombre"
                    data-testid="consulta-nombre-input"
                    value={formData.nombre}
                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                    className="h-11 rounded-xl"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="telefono">Teléfono *</Label>
                  <Input
                    id="telefono"
                    data-testid="consulta-telefono-input"
                    value={formData.telefono}
                    onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                    className="h-11 rounded-xl"
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  data-testid="consulta-email-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="h-11 rounded-xl"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="fecha_evento">Fecha Evento *</Label>
                  <Input
                    id="fecha_evento"
                    data-testid="consulta-fecha-input"
                    type="date"
                    value={formData.fecha_evento}
                    onChange={(e) => setFormData({ ...formData, fecha_evento: e.target.value })}
                    className="h-11 rounded-xl"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tipo_evento">Tipo Evento *</Label>
                  <Select
                    value={formData.tipo_evento}
                    onValueChange={(value) => setFormData({ ...formData, tipo_evento: value })}
                  >
                    <SelectTrigger data-testid="consulta-tipo-select" className="h-11 rounded-xl">
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIPOS_EVENTO.map((tipo) => (
                        <SelectItem key={tipo} value={tipo}>
                          {tipo}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="fuente">Fuente *</Label>
                <Select
                  value={formData.fuente}
                  onValueChange={(value) => setFormData({ ...formData, fuente: value })}
                >
                  <SelectTrigger className="h-11 rounded-xl">
                    <SelectValue placeholder="Seleccionar fuente" />
                  </SelectTrigger>
                  <SelectContent>
                    {FUENTES_CONSULTA.map((fuente) => (
                      <SelectItem key={fuente.value} value={fuente.value}>
                        {fuente.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ubicacion">Ubicación *</Label>
                <Input
                  id="ubicacion"
                  data-testid="consulta-ubicacion-input"
                  value={formData.ubicacion}
                  onChange={(e) => setFormData({ ...formData, ubicacion: e.target.value })}
                  placeholder="Dirección del evento"
                  className="h-11 rounded-xl"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="notas">Notas</Label>
                <Textarea
                  id="notas"
                  data-testid="consulta-notas-input"
                  value={formData.notas}
                  onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
                  placeholder="Información adicional..."
                  className="rounded-xl resize-none"
                  rows={3}
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="rounded-full"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  data-testid="consulta-submit-btn"
                  className="rounded-full bg-primary"
                  disabled={submitting}
                >
                  {submitting ? "Guardando..." : "Crear Consulta"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            data-testid="search-consultas-input"
            placeholder="Buscar por nombre, email o ubicación..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-11 rounded-xl"
          />
        </div>
        <Select value={filterEstado || "all"} onValueChange={(v) => setFilterEstado(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="filter-estado-select" className="w-48 h-11 rounded-xl">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Todos los estados" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {ESTADOS.map((estado) => (
              <SelectItem key={estado.value} value={estado.value}>
                {estado.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterFuente || "all"} onValueChange={(v) => setFilterFuente(v === "all" ? "" : v)}>
          <SelectTrigger className="w-48 h-11 rounded-xl">
            <Globe className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Todas las fuentes" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las fuentes</SelectItem>
            {FUENTES_CONSULTA.map((fuente) => (
              <SelectItem key={fuente.value} value={fuente.value}>
                {fuente.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Kanban Board */}
      <div className="overflow-x-auto pb-4 -mx-6 px-6">
        <div className="flex gap-4 min-w-max">
          {ESTADOS.filter(e => e.value !== "convertida" && e.value !== "descartada").map((estado) => (
            <div
              key={estado.value}
              className="w-80 flex-shrink-0"
            >
              <div className={`rounded-t-xl p-3 ${estado.color.replace("text-", "bg-").split(" ")[0]}/20`}>
                <div className="flex items-center justify-between">
                  <Badge className={estado.color} variant="outline">
                    {estado.label}
                  </Badge>
                  <span className="text-sm font-medium text-muted-foreground">
                    {groupedConsultas[estado.value]?.length || 0}
                  </span>
                </div>
              </div>
              <div className="bg-muted/30 rounded-b-xl p-3 min-h-[400px] space-y-3">
                {groupedConsultas[estado.value]?.map((consulta) => {
                  const diasRestantes = getDiasRestantes(consulta);
                  const highlight = getDiasHighlightStyles(diasRestantes);
                  return (
                    <Link
                      key={consulta.id}
                      to={`/consultas/${consulta.id}`}
                      data-testid={`consulta-card-${consulta.id}`}
                    >
                      <Card className={`shadow-soft hover:shadow-lg transition-all duration-300 cursor-pointer group rounded-2xl ${highlight.card}`}>
                        <CardContent className="p-4 space-y-3">
                          <div className="flex items-center justify-between gap-2">
                            <h3 className="font-semibold text-primary group-hover:text-secondary transition-colors">
                              {consulta.nombre}
                            </h3>
                            {diasRestantes !== null && (
                              <span className={`text-xs px-2 py-1 rounded-full font-medium ${highlight.badge}`}>
                                {formatDaysRemaining(diasRestantes)}
                              </span>
                            )}
                          </div>
                          <div className="space-y-1 text-sm text-muted-foreground">
                            <div className="flex items-center gap-2">
                              <Globe className="w-3 h-3" strokeWidth={1.5} />
                              <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
                                {FUENTES_CONSULTA.find(f => f.value === consulta.fuente)?.label || consulta.fuente}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Calendar className="w-3 h-3" strokeWidth={1.5} />
                              <span>{formatDate(consulta.fecha_evento)}</span>
                            </div>
                            {diasRestantes !== null && (
                              <div className="flex items-center gap-2">
                                <Clock className="w-3 h-3" strokeWidth={1.5} />
                                <span className="text-xs text-muted-foreground">
                                  {formatDaysRemaining(diasRestantes)}
                                </span>
                              </div>
                            )}
                            <div className="flex items-center gap-2">
                              <MapPin className="w-3 h-3" strokeWidth={1.5} />
                              <span className="truncate">{consulta.ubicacion}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Phone className="w-3 h-3" strokeWidth={1.5} />
                              <span>{consulta.telefono}</span>
                            </div>
                          </div>
                          <div className="flex items-center justify-between pt-3 border-t border-border/40">
                            <Badge variant="outline" className="text-xs">
                              {consulta.tipo_evento}
                            </Badge>
                            <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  );
                })}
                {groupedConsultas[estado.value]?.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground text-sm">
                    Sin consultas
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Converted and Discarded Section */}
      <div className="grid md:grid-cols-2 gap-6 mt-8">
        {["convertida", "descartada"].map((estadoKey) => {
          const estado = ESTADOS.find(e => e.value === estadoKey);
          return (
            <div key={estadoKey}>
              <div className="flex items-center gap-2 mb-3">
                <Badge className={estado.color} variant="outline">
                  {estado.label}
                </Badge>
                <span className="text-sm text-muted-foreground">
                  ({groupedConsultas[estadoKey]?.length || 0})
                </span>
              </div>
              <div className="space-y-2">
                {groupedConsultas[estadoKey]?.slice(0, 3).map((consulta) => (
                  <Link
                    key={consulta.id}
                    to={`/consultas/${consulta.id}`}
                    className="block p-3 bg-muted/30 rounded-xl hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-primary">{consulta.nombre}</p>
                        <p className="text-sm text-muted-foreground">{consulta.tipo_evento} - {formatDate(consulta.fecha_evento)}</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </Link>
                ))}
                {groupedConsultas[estadoKey]?.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Sin consultas {estado.label.toLowerCase()}s
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
